import React, { useState } from "react";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle } from "@fortawesome/free-solid-svg-icons";

/**
 * VerificationBadge component displays a blue checkmark for verified users
 *
 * Features:
 * - Blue checkmark icon for verified users
 * - Tooltip functionality showing "Verified User" on hover
 * - Multiple size variants (small, medium, large)
 * - Consistent styling across application
 *
 * Requirements: 5.1, 5.2, 5.3, 5.4
 */
const VerificationBadge = ({
  verified = false,
  size = "medium",
  showTooltip = true,
  className = "",
  ...rest
}) => {
  const [showTooltipState, setShowTooltipState] = useState(false);

  // Don't render anything if user is not verified
  if (!verified) {
    return null;
  }

  // Size-specific styles for the icon
  const sizeStyles = {
    small: {
      icon: "text-sm",
      tooltip: "text-xs px-2 py-1",
    },
    medium: {
      icon: "text-base",
      tooltip: "text-sm px-3 py-2",
    },
    large: {
      icon: "text-lg",
      tooltip: "text-base px-4 py-2",
    },
  };

  const currentSize = sizeStyles[size] || sizeStyles.medium;

  // Base classes for the verification badge
  const badgeClasses = `
    inline-flex items-center justify-center
    text-blue-500
    ${currentSize.icon}
    ${className}
  `
    .trim()
    .replace(/\s+/g, " ");

  // Tooltip component
  const tooltip = showTooltip && showTooltipState && (
    <div className="absolute z-50 bottom-full mb-2 left-1/2 transform -translate-x-1/2">
      <div
        className={`
          bg-gray-900 text-white rounded-md shadow-lg whitespace-nowrap
          ${currentSize.tooltip}
        `}
      >
        Verified User
        {/* Tooltip arrow */}
        <div className="absolute top-full left-1/2 transform -translate-x-1/2">
          <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
        </div>
      </div>
    </div>
  );

  return (
    <div
      className="relative inline-block"
      onMouseEnter={() => showTooltip && setShowTooltipState(true)}
      onMouseLeave={() => showTooltip && setShowTooltipState(false)}
      {...rest}
    >
      <FontAwesomeIcon
        icon={faCheckCircle}
        className={badgeClasses}
        aria-label="Verified User"
      />
      {tooltip}
    </div>
  );
};

VerificationBadge.propTypes = {
  verified: PropTypes.bool,
  size: PropTypes.oneOf(["small", "medium", "large"]),
  showTooltip: PropTypes.bool,
  className: PropTypes.string,
};

export default VerificationBadge;
